import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Stack,
  Button,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { ArrowOutward } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { apiService } from '../services/api';
import PartnersStrip from '../components/public/PartnersStrip';
import { RootState } from '../store';

/* =============================================================================
 * DESIGN TOKENS
 * ---------------------------------------------------------------------------
 * Idénticos a LandingPage.tsx — esta página comparte la misma voz editorial
 * (papel crema cálido, tinta negra, amarillo de alerta de inventario). Se
 * mantiene desacoplada del theme MUI in-app a propósito, igual que la landing.
 * ========================================================================== */
const CREAM = '#F5EFE0';
const CREAM_SOFT = '#FAF6EC';
const INK = '#0A0A0A';
const YELLOW = '#F6D058';
const YELLOW_DEEP = '#E8B923';
const INDIGO = '#1e40af';
const RULE = 'rgba(10,10,10,0.12)';
const RULE_HARD = 'rgba(10,10,10,0.22)';

const DISPLAY = '"Instrument Serif", "Times New Roman", Georgia, serif';
const SANS = '"Work Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
const MONO = '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace';

/* =============================================================================
 * TIPOS — espejo de PublicPlansController.List() (GET /api/subscription-plans)
 * ========================================================================== */
interface Plan {
  id: number;
  code: string;
  name: string;
  description?: string;
  price: number;
  originalPrice?: number | null;
  currency: string;
  trialDays: number;
  isPopular: boolean;
  features?: string[];
}

type Billing = 'annual' | 'monthly';

/* Descuento anual de marketing: pagás 12 meses pero mostramos el equivalente
 * con 2 meses bonificados. El checkout real es mensual (el backend no maneja
 * ciclos anuales todavía), así que el "anual" es framing honesto: 10 pagos. */
const ANNUAL_FREE_MONTHS = 2;
const ANNUAL_DISCOUNT_PCT = Math.round((ANNUAL_FREE_MONTHS / 12) * 100); // ~17%

/* Resultado / IMPACT BULLETS */
const IMPACT = [
  {
    k: '01',
    t: 'Reportes de rentabilidad',
    d: 'Margen real por producto, por rubro y por día. Sabés qué te deja plata y qué te la come.',
  },
  {
    k: '02',
    t: 'Alertas de stock',
    d: 'Te avisamos antes de quedarte sin lo que más vendés. Reposición sugerida por velocidad real de venta.',
  },
  {
    k: '03',
    t: 'Ventas por canal',
    d: 'Mostrador, WhatsApp, delivery y online en un solo tacho. Una sola fuente de verdad para tu stock.',
  },
  {
    k: '04',
    t: 'Cierre de caja en segundos',
    d: 'Arqueo, ingresos y egresos del día sin calculadora. El Z sale solo y cuadra siempre.',
  },
  {
    k: '05',
    t: 'Detectá lo que no rota',
    d: 'El sistema marca los productos estancados para que liberes capital y compres lo que sí gira.',
  },
  {
    k: '06',
    t: 'Facturación ARCA integrada',
    d: 'Emitís comprobantes fiscales desde la misma venta. Sin cargar dos veces, sin saltar de sistema.',
  },
];

/* =============================================================================
 * HELPERS — copiados textualmente de LandingPage.tsx
 * ========================================================================== */
const Reveal: React.FC<{
  children: React.ReactNode;
  delay?: number;
  y?: number;
  as?: React.ElementType;
}> = ({ children, delay = 0, y = 24, as = 'div' }) => {
  const MotionComponent = motion(as as any);
  return (
    <MotionComponent
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.7, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
    >
      {children}
    </MotionComponent>
  );
};

const Section: React.FC<{
  id?: string;
  bg?: string;
  color?: string;
  children: React.ReactNode;
  sx?: object;
}> = ({ id, bg, color, children, sx = {} }) => (
  <Box
    component="section"
    id={id}
    sx={{
      position: 'relative',
      bgcolor: bg,
      color,
      py: { xs: 8, md: 11 },
      scrollMarginTop: 80,
      ...sx,
    }}
  >
    <Container maxWidth="lg">{children}</Container>
  </Box>
);

const SectionLabel: React.FC<{ idx: string; children: React.ReactNode; color?: string }> = ({
  idx,
  children,
  color,
}) => (
  <Stack direction="row" spacing={2} alignItems="baseline" sx={{ mb: 3 }}>
    <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', letterSpacing: '0.18em', color: color ?? INK, opacity: 0.6 }}>
      {idx}
    </Typography>
    <Box sx={{ flex: 1, height: 1, bgcolor: color ? 'rgba(255,255,255,0.25)' : RULE }} />
    <Typography
      sx={{
        fontFamily: SANS,
        fontSize: '0.72rem',
        fontWeight: 600,
        letterSpacing: '0.22em',
        textTransform: 'uppercase',
        color: color ?? INK,
        opacity: 0.8,
      }}
    >
      {children}
    </Typography>
  </Stack>
);

const pillBtn = (variant: 'solid' | 'outline' | 'ghost' = 'solid', on: 'light' | 'dark' = 'light') => {
  const isDark = on === 'dark';
  if (variant === 'solid') {
    return {
      bgcolor: INK,
      color: CREAM_SOFT,
      borderRadius: 999,
      px: 3,
      py: 1.4,
      fontFamily: SANS,
      fontSize: '0.88rem',
      fontWeight: 600,
      letterSpacing: '0.01em',
      textTransform: 'none' as const,
      border: '1px solid',
      borderColor: INK,
      boxShadow: 'none',
      '&:hover': { bgcolor: INDIGO, borderColor: INDIGO, boxShadow: 'none' },
    };
  }
  if (variant === 'outline') {
    return {
      bgcolor: 'transparent',
      color: isDark ? CREAM_SOFT : INK,
      borderRadius: 999,
      px: 3,
      py: 1.4,
      fontFamily: SANS,
      fontSize: '0.88rem',
      fontWeight: 600,
      textTransform: 'none' as const,
      border: '1px solid',
      borderColor: isDark ? 'rgba(255,255,255,0.3)' : RULE_HARD,
      '&:hover': {
        bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(10,10,10,0.04)',
        borderColor: isDark ? CREAM_SOFT : INK,
      },
    };
  }
  return {
    bgcolor: 'transparent',
    color: isDark ? CREAM_SOFT : INK,
    borderRadius: 999,
    px: 2,
    py: 1,
    fontFamily: SANS,
    fontSize: '0.88rem',
    fontWeight: 500,
    textTransform: 'none' as const,
    '&:hover': { bgcolor: 'transparent', opacity: 0.7 },
  };
};

const fmt = (n: number) => n.toLocaleString('es-AR');

/* =============================================================================
 * MASTHEAD — barra minimal, en línea con la landing
 * ========================================================================== */
const Masthead: React.FC = () => (
  <Box
    component="header"
    sx={{ position: 'sticky', top: 0, zIndex: 30, bgcolor: CREAM, borderBottom: `1px solid ${RULE}` }}
  >
    <Container maxWidth="lg">
      <Stack direction="row" alignItems="center" sx={{ py: 2.2 }} spacing={2}>
        <Box component={RouterLink} to="/" sx={{ textDecoration: 'none', color: INK, display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography sx={{ fontFamily: DISPLAY, fontSize: '1.55rem', lineHeight: 1, fontWeight: 400 }}>DashFlow</Typography>
          <Typography sx={{ fontFamily: DISPLAY, fontStyle: 'italic', fontSize: '1.55rem', lineHeight: 1, color: YELLOW_DEEP }}>Pro</Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button component={RouterLink} to="/login" sx={pillBtn('ghost')}>
          Ingresar
        </Button>
        <Button
          component={RouterLink}
          to="/register"
          sx={pillBtn('solid')}
          endIcon={<ArrowOutward sx={{ fontSize: 16 }} />}
        >
          Empezar gratis
        </Button>
      </Stack>
    </Container>
  </Box>
);

/* =============================================================================
 * 2 · VISION HEADLINE + 3 · IMPACT BULLETS
 * ========================================================================== */
const Vision: React.FC = () => (
  <Section bg={CREAM_SOFT}>
    <Reveal>
      <SectionLabel idx="§ 01">El resultado</SectionLabel>
      <Typography
        component="h1"
        sx={{
          fontFamily: DISPLAY,
          fontWeight: 400,
          fontSize: { xs: '2.6rem', md: '4.4rem' },
          lineHeight: 1.0,
          letterSpacing: '-0.025em',
          maxWidth: 960,
          mb: { xs: 3, md: 4 },
        }}
      >
        Controlá tu stock y tus ventas,{' '}
        <Box component="em" sx={{ fontStyle: 'italic', color: YELLOW_DEEP }}>
          aumentá tu ganancia.
        </Box>
      </Typography>
      <Typography sx={{ fontFamily: SANS, fontSize: { xs: '1.05rem', md: '1.2rem' }, color: 'rgba(10,10,10,0.7)', maxWidth: 640, mb: { xs: 6, md: 8 } }}>
        DashFlow convierte tu mostrador en datos: qué se vende, qué deja margen y qué reponer.
        Menos planillas, menos faltantes, más plata en la caja a fin de mes.
      </Typography>
    </Reveal>

    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 0, border: `1px solid ${RULE_HARD}` }}>
      {IMPACT.map((b, i) => (
        <Reveal key={b.k} delay={(i % 3) * 0.08}>
          <Box
            sx={{
              p: 3.5,
              height: '100%',
              borderRight: { md: (i + 1) % 3 === 0 ? 'none' : `1px solid ${RULE}` },
              // Línea inferior salvo en la última fila del grid de 3 columnas (i >= 3).
              borderBottom: { xs: i < IMPACT.length - 1 ? `1px solid ${RULE}` : 'none', md: i < 3 ? `1px solid ${RULE}` : 'none' },
              bgcolor: CREAM,
            }}
          >
            <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', letterSpacing: '0.18em', color: YELLOW_DEEP, mb: 1.5 }}>
              {b.k}
            </Typography>
            <Typography sx={{ fontFamily: DISPLAY, fontSize: '1.5rem', lineHeight: 1.1, mb: 1 }}>{b.t}</Typography>
            <Typography sx={{ fontFamily: SANS, fontSize: '0.92rem', lineHeight: 1.55, color: 'rgba(10,10,10,0.68)' }}>
              {b.d}
            </Typography>
          </Box>
        </Reveal>
      ))}
    </Box>
  </Section>
);

/* =============================================================================
 * 4 · PRICING TIERS + 5 · CTA (cableado al checkout)
 * ========================================================================== */
const PricingTiers: React.FC<{
  plans: Plan[];
  loading: boolean;
  error: string;
  isAuthenticated: boolean;
  subscribing: string | null;
  onSubscribe: (code: string) => void;
}> = ({ plans, loading, error, isAuthenticated, subscribing, onSubscribe }) => {
  const [billing, setBilling] = useState<Billing>('annual');

  return (
    <Section bg={CREAM} id="pricing">
      <Reveal>
        <SectionLabel idx="§ 02">Planes y precios</SectionLabel>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'flex-end' }}
          spacing={3}
          sx={{ mb: { xs: 5, md: 7 } }}
        >
          <Typography sx={{ fontFamily: DISPLAY, fontSize: { xs: '2.2rem', md: '3.4rem' }, lineHeight: 1.02, letterSpacing: '-0.02em', maxWidth: 680 }}>
            Empezá <Box component="em" sx={{ fontStyle: 'italic' }}>gratis</Box>. Sin tarjeta. Pasás a un plan pago cuando lo necesites.
          </Typography>

          {/* Toggle Anual / Mensual */}
          <Stack spacing={1} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
            <ToggleButtonGroup
              exclusive
              value={billing}
              onChange={(_, v) => v && setBilling(v)}
              sx={{
                bgcolor: CREAM_SOFT,
                border: `1px solid ${INK}`,
                borderRadius: 999,
                p: 0.5,
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: 999,
                  px: 2.4,
                  py: 0.8,
                  fontFamily: SANS,
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  textTransform: 'none',
                  color: 'rgba(10,10,10,0.6)',
                  '&.Mui-selected': {
                    bgcolor: INK,
                    color: CREAM_SOFT,
                    '&:hover': { bgcolor: INK },
                  },
                },
              }}
            >
              <ToggleButton value="annual">Anual</ToggleButton>
              <ToggleButton value="monthly">Mensual</ToggleButton>
            </ToggleButtonGroup>
            {billing === 'annual' && (
              <Box sx={{ bgcolor: YELLOW, color: INK, px: 1.4, py: 0.4, border: `1px solid ${INK}`, fontFamily: MONO, fontSize: '0.66rem', letterSpacing: '0.12em' }}>
                AHORRÁ {ANNUAL_DISCOUNT_PCT}% · 2 MESES BONIFICADOS
              </Box>
            )}
          </Stack>
        </Stack>
      </Reveal>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 0, border: `1px solid ${RULE_HARD}` }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: INK }} />
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${Math.min(plans.length, 2)}, 1fr)`, lg: `repeat(${Math.min(plans.length, 4)}, 1fr)` }, gap: 2.5, alignItems: 'stretch' }}>
          {plans.map((p, i) => {
            const dark = p.isPopular;
            const isFree = p.code === 'free' || p.price === 0;
            // Precio mostrado según ciclo. Anual = equivalente mensual con 2 meses bonificados.
            const annualMonthly = Math.round((p.price * (12 - ANNUAL_FREE_MONTHS)) / 12);
            const shown = billing === 'annual' ? annualMonthly : p.price;
            const annualTotal = p.price * (12 - ANNUAL_FREE_MONTHS);
            const ctaLabel = isFree
              ? (isAuthenticated ? 'Ir a mi cuenta' : 'Empezar gratis')
              : isAuthenticated ? `Suscribirme a ${p.name.replace('Plan ', '')}` : `Empezar con ${p.name.replace('Plan ', '')}`;

            return (
              <Reveal key={p.code} delay={i * 0.1}>
                <Box
                  sx={{
                    position: 'relative',
                    height: '100%',
                    bgcolor: dark ? INK : CREAM_SOFT,
                    color: dark ? CREAM_SOFT : INK,
                    border: `1px solid ${INK}`,
                    p: { xs: 3, md: 3.5 },
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.4s cubic-bezier(0.21,0.47,0.32,0.98)',
                    '&:hover': { transform: 'translateY(-6px)' },
                    ...(dark && { boxShadow: `6px 6px 0 ${YELLOW}` }),
                  }}
                >
                  {/* Library-card stub */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5, pb: 1.5, borderBottom: dark ? '1px solid rgba(255,255,255,0.2)' : `1px dashed ${RULE_HARD}` }}>
                    <Typography sx={{ fontFamily: MONO, fontSize: '0.68rem', letterSpacing: '0.2em', opacity: 0.7 }}>
                      PLAN {String(i + 1).padStart(2, '0')}
                    </Typography>
                    {p.isPopular ? (
                      <Box sx={{ bgcolor: YELLOW, color: INK, px: 1.2, py: 0.3, fontFamily: MONO, fontSize: '0.62rem', letterSpacing: '0.14em' }}>
                        MÁS ELEGIDO
                      </Box>
                    ) : p.code === 'ia' ? (
                      <Box sx={{ bgcolor: 'transparent', color: dark ? YELLOW : INK, px: 1.2, py: 0.3, border: `1px solid ${dark ? 'rgba(255,255,255,0.4)' : RULE_HARD}`, fontFamily: MONO, fontSize: '0.62rem', letterSpacing: '0.14em' }}>
                        CON IA
                      </Box>
                    ) : null}
                  </Stack>

                  <Typography sx={{ fontFamily: DISPLAY, fontSize: '2.2rem', lineHeight: 1, mb: 1 }}>
                    {p.name.replace('Plan ', '')}
                  </Typography>
                  {p.description && (
                    <Typography sx={{ fontFamily: SANS, fontStyle: 'italic', fontSize: '0.92rem', color: dark ? 'rgba(255,255,255,0.75)' : 'rgba(10,10,10,0.65)', mb: 2.5, minHeight: 42 }}>
                      {p.description}
                    </Typography>
                  )}

                  {/* Precio */}
                  <Stack direction="row" alignItems="baseline" spacing={0.6} sx={{ mb: 0.5 }}>
                    <Typography sx={{ fontFamily: MONO, fontSize: '0.85rem', opacity: 0.7 }}>$</Typography>
                    <Typography sx={{ fontFamily: DISPLAY, fontSize: { xs: '3.2rem', md: '3.6rem' }, lineHeight: 1 }}>
                      {fmt(shown)}
                    </Typography>
                    <Typography sx={{ fontFamily: MONO, fontSize: '0.78rem', opacity: 0.7 }}>
                      {p.currency}/mes
                    </Typography>
                  </Stack>
                  {isFree ? (
                    <Typography sx={{ fontFamily: SANS, fontSize: '0.8rem', color: dark ? 'rgba(255,255,255,0.6)' : 'rgba(10,10,10,0.55)', mb: 2.5 }}>
                      Gratis para siempre · sin tarjeta
                    </Typography>
                  ) : billing === 'annual' ? (
                    <Typography sx={{ fontFamily: SANS, fontSize: '0.8rem', color: dark ? 'rgba(255,255,255,0.6)' : 'rgba(10,10,10,0.55)', mb: 2.5 }}>
                      Facturado anual: ${fmt(annualTotal)} {p.currency}/año · ahorrás ${fmt(p.price * ANNUAL_FREE_MONTHS)}
                    </Typography>
                  ) : (
                    <Typography sx={{ fontFamily: SANS, fontSize: '0.8rem', color: dark ? 'rgba(255,255,255,0.6)' : 'rgba(10,10,10,0.55)', mb: 2.5 }}>
                      {p.originalPrice && p.originalPrice > p.price ? (
                        <>
                          Antes <Box component="span" sx={{ textDecoration: 'line-through' }}>${fmt(p.originalPrice)}</Box> · facturación mensual
                        </>
                      ) : (
                        'Facturación mensual, sin permanencia.'
                      )}
                    </Typography>
                  )}


                  {/* Features */}
                  <Stack spacing={1.1} sx={{ mb: 3.5, flex: 1 }}>
                    {(p.features || []).map((f) => (
                      <Stack key={f} direction="row" spacing={1.2} alignItems="baseline">
                        <Box sx={{ width: 6, height: 6, mt: 0.6, bgcolor: dark ? YELLOW : INK, flexShrink: 0 }} />
                        <Typography sx={{ fontFamily: SANS, fontSize: '0.9rem', lineHeight: 1.45 }}>{f}</Typography>
                      </Stack>
                    ))}
                  </Stack>

                  {/* CTA cableado al checkout */}
                  <Button
                    onClick={() => onSubscribe(p.code)}
                    disabled={subscribing === p.code}
                    sx={
                      dark
                        ? { ...pillBtn('solid'), bgcolor: YELLOW, color: INK, borderColor: YELLOW, '&:hover': { bgcolor: YELLOW_DEEP, borderColor: YELLOW_DEEP } }
                        : pillBtn('outline')
                    }
                    endIcon={subscribing === p.code ? undefined : <ArrowOutward sx={{ fontSize: 16 }} />}
                    fullWidth
                  >
                    {subscribing === p.code ? <CircularProgress size={20} sx={{ color: dark ? INK : INK }} /> : ctaLabel}
                  </Button>
                </Box>
              </Reveal>
            );
          })}
        </Box>
      )}

      <Reveal>
        <Typography sx={{ fontFamily: SANS, fontSize: '0.86rem', color: 'rgba(10,10,10,0.6)', textAlign: 'center', mt: 4, maxWidth: 720, mx: 'auto' }}>
          Todos los planes incluyen ventas con lector de código de barras, cuentas corrientes de clientes
          y migración asistida de tu inventario. Facturación electrónica de ARCA como adicional opcional.
        </Typography>
      </Reveal>
    </Section>
  );
};

/* =============================================================================
 * CTA motivacional — slab amarillo (idéntico idiom a la landing)
 * ========================================================================== */
const FinalCTA: React.FC<{ isAuthenticated: boolean; onPrimary: () => void; primaryLabel: string; busy: boolean }> = ({
  isAuthenticated,
  onPrimary,
  primaryLabel,
  busy,
}) => (
  <Box sx={{ bgcolor: YELLOW, color: INK, py: { xs: 9, md: 12 }, position: 'relative', overflow: 'hidden', borderTop: `1px solid ${INK}`, borderBottom: `1px solid ${INK}` }}>
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        opacity: 0.12,
        backgroundImage: `repeating-linear-gradient(135deg, ${INK} 0 1px, transparent 1px 36px)`,
        pointerEvents: 'none',
      }}
    />
    <Container maxWidth="lg" sx={{ position: 'relative' }}>
      <Reveal>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'flex-end' }} spacing={5}>
          <Box sx={{ maxWidth: 780 }}>
            <Typography sx={{ fontFamily: MONO, fontSize: '0.78rem', letterSpacing: '0.18em', mb: 3 }}>
              § FIN · TU NEGOCIO, BAJO CONTROL
            </Typography>
            <Typography sx={{ fontFamily: DISPLAY, fontSize: { xs: '3rem', sm: '4.2rem', md: '5.6rem' }, lineHeight: 0.92, letterSpacing: '-0.025em', fontWeight: 400 }}>
              Dejá de adivinar. <Box component="em" sx={{ fontStyle: 'italic' }}>Empezá a vender mejor.</Box>
            </Typography>
            <Typography sx={{ fontFamily: SANS, fontSize: { xs: '1rem', md: '1.18rem' }, mt: 3, maxWidth: 560, color: 'rgba(10,10,10,0.82)' }}>
              Gratis para siempre, sin tarjeta. Cargás tu inventario hoy y mañana ya sabés exactamente qué te deja plata.
            </Typography>
          </Box>

          <Stack spacing={2} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
            <Button
              onClick={onPrimary}
              disabled={busy}
              sx={{ ...pillBtn('solid'), px: 4, py: 2, fontSize: '1rem' }}
              endIcon={busy ? undefined : <ArrowOutward sx={{ fontSize: 18 }} />}
            >
              {busy ? <CircularProgress size={22} sx={{ color: CREAM_SOFT }} /> : primaryLabel}
            </Button>
            <Typography sx={{ fontFamily: MONO, fontSize: '0.74rem', letterSpacing: '0.14em', opacity: 0.7 }}>
              {isAuthenticated ? 'PAGO SEGURO · MERCADO PAGO' : 'SIN TARJETA · SIN COMPROMISO'}
            </Typography>
          </Stack>
        </Stack>
      </Reveal>
    </Container>
  </Box>
);

/* =============================================================================
 * 6 · FOOTER DE CONFIANZA
 * ========================================================================== */
const TrustFooter: React.FC = () => (
  <Box component="footer" sx={{ bgcolor: INK, color: CREAM_SOFT, py: { xs: 5, md: 7 } }}>
    <Container maxWidth="lg">
      <PartnersStrip />
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={4}>
        <Box sx={{ maxWidth: 380 }}>
          <Typography sx={{ fontFamily: DISPLAY, fontSize: '1.8rem', lineHeight: 1, mb: 1.5 }}>
            Dash<Box component="em" sx={{ fontStyle: 'italic', color: YELLOW }}>Flow</Box>
          </Typography>
          <Typography sx={{ fontFamily: SANS, fontSize: '0.88rem', color: 'rgba(255,255,255,0.62)', mb: 2.5 }}>
            Gestión de stock, ventas y caja para comercios y PyMEs. Pago seguro procesado por Mercado Pago.
            Tus datos viajan cifrados y nunca se comparten.
          </Typography>
          <Box sx={{ fontFamily: MONO, fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', lineHeight: 2 }}>
            <Box>▸ PAGO · MERCADO PAGO (PCI-DSS)</Box>
            <Box>▸ DATOS · CIFRADOS · AISLADOS POR CUENTA</Box>
            <Box>▸ SOPORTE · ES (HUMANO)</Box>
          </Box>
        </Box>

        <Stack direction="row" spacing={6}>
          <Box>
            <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', mb: 2 }}>
              Confianza
            </Typography>
            <FooterLink to="/terminos">Términos y Condiciones</FooterLink>
            <FooterLink to="/privacidad">Política de Privacidad</FooterLink>
            <FooterLink to="/ayuda">Centro de ayuda</FooterLink>
            <FooterLink to="/">Volver al inicio</FooterLink>
          </Box>
          <Box>
            <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', mb: 2 }}>
              Cuenta
            </Typography>
            <FooterLink to="/login">Ingresar</FooterLink>
            <FooterLink to="/register">Crear cuenta</FooterLink>
          </Box>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mt: { xs: 5, md: 7 }, pt: 3, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.14em', color: 'rgba(255,255,255,0.45)' }}>
          © {new Date().getFullYear()} · DASHFLOW · TODOS LOS DERECHOS RESERVADOS
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#34d399' }} />
          <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)' }}>
            status: all systems go
          </Typography>
        </Stack>
      </Stack>
    </Container>
  </Box>
);

const FooterLink: React.FC<{ children: React.ReactNode; to: string }> = ({ children, to }) => (
  <Box
    component={RouterLink}
    to={to}
    sx={{
      display: 'block',
      fontFamily: SANS,
      fontSize: '0.9rem',
      color: 'rgba(255,255,255,0.82)',
      textDecoration: 'none',
      mb: 1,
      transition: 'color 0.2s',
      '&:hover': { color: YELLOW },
    }}
  >
    {children}
  </Box>
);

/* =============================================================================
 * PAGE
 * ========================================================================== */
const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subscribing, setSubscribing] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const api = (apiService as any).api;
        const res = await api.get('/subscription-plans');
        // Destacado: siempre el primer plan pago (el que sigue al Gratis), no el isPopular de la base.
        const list: Plan[] = res.data || [];
        const firstPaid = list.findIndex((p) => p.price > 0);
        if (alive) setPlans(list.map((p, i) => ({ ...p, isPopular: i === firstPaid })));
      } catch (e: any) {
        if (alive) setError(e?.response?.data?.error || 'No pudimos cargar los planes. Probá de nuevo en un momento.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /**
   * Cableado al checkout existente:
   *  - Logueado → POST /api/checkout/create-subscription { planCode } → redirige
   *    al checkoutUrl de Mercado Pago (mismo flujo que SubscriptionUpgrade.tsx).
   *  - Anónimo → lo mandamos a /register, que crea la cuenta en el plan Gratis (no se
   *    puede crear la suscripción sin cuenta: el endpoint es [Authorize]).
   */
  const handleSubscribe = async (planCode: string) => {
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }
    // El plan Gratis no pasa por el checkout: ya es el plan de toda cuenta nueva.
    if (planCode === 'free') {
      navigate('/dashboard');
      return;
    }
    setError('');
    setSubscribing(planCode);
    try {
      const api = (apiService as any).api;
      const res = await api.post('/checkout/create-subscription', { planCode });
      if (res.data?.checkoutUrl) {
        window.location.href = res.data.checkoutUrl;
      } else {
        setError('El proveedor de pagos no devolvió una URL de checkout. Probá de nuevo.');
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || 'No pudimos iniciar el pago. Probá de nuevo en un momento.');
    } finally {
      setSubscribing(null);
    }
  };

  // CTA final (logueado): el plan pago destacado o el primer plan pago.
  const featured = useMemo(
    () => plans.find((p) => p.isPopular && p.code !== 'free') || plans.find((p) => p.code !== 'free'),
    [plans],
  );
  const handleFinalCTA = () => {
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }
    if (featured) handleSubscribe(featured.code);
  };

  return (
    <Box sx={{ bgcolor: CREAM, color: INK, fontFamily: SANS, overflowX: 'hidden', minHeight: '100vh' }}>
      <Masthead />
      <Vision />
      <PricingTiers
        plans={plans}
        loading={loading}
        error={error}
        isAuthenticated={isAuthenticated}
        subscribing={subscribing}
        onSubscribe={handleSubscribe}
      />
      <FinalCTA
        isAuthenticated={isAuthenticated}
        onPrimary={handleFinalCTA}
        primaryLabel={isAuthenticated ? (featured ? `Suscribirme a ${featured.name.replace('Plan ', '')}` : 'Suscribirme') : 'Empezar gratis'}
        busy={!!featured && subscribing === featured.code}
      />
      <TrustFooter />
    </Box>
  );
};

export default PricingPage;
